import { useState, useEffect, useRef } from "react";
import { Users, Upload, Download, Search, Trash2, Eye, EyeOff, Plus, X, Check, AlertCircle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";

import { API } from "../../config/api";

export default function AdminMembers() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bulkData, setBulkData] = useState("");
  const [bulkPreview, setBulkPreview] = useState([]);
  const [bulkErrors, setBulkErrors] = useState([]);
  const fileInputRef = useRef(null);

  const [newMember, setNewMember] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    gender: "",
    streetAddress: "",
    city: "",
    postalCode: "",
    province: "Ontario",
    country: "Canada"
  });

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await fetch(`${API}/admin/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMembers(data);
      }
    } catch (error) {
      console.error("Error fetching members:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("admin_token");
      const response = await fetch(`${API}/admin/members`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newMember)
      });

      if (response.ok) {
        toast.success("Member added successfully");
        setShowAddModal(false);
        setNewMember({
          name: "",
          email: "",
          password: "",
          phone: "",
          gender: "",
          streetAddress: "",
          city: "",
          postalCode: "",
          province: "Ontario",
          country: "Canada"
        });
        fetchMembers();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add member");
      }
    } catch (error) {
      toast.error("Error adding member");
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm("Are you sure you want to delete this member?")) return;
    
    try {
      const token = localStorage.getItem("admin_token");
      const response = await fetch(`${API}/admin/members/${memberId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Member deleted");
        fetchMembers();
      } else {
        toast.error("Failed to delete member");
      }
    } catch (error) {
      toast.error("Error deleting member");
    }
  };

  const parseCSV = (text) => {
    const lines = text.trim().split("\n");
    if (lines.length < 2) {
      return { data: [], errors: ["CSV must have at least a header row and one data row"] };
    }

    const headers = lines[0].split(",").map(h => h.trim().toLowerCase());
    const requiredFields = ["name", "email"];
    const missingFields = requiredFields.filter(f => !headers.includes(f));
    
    if (missingFields.length > 0) {
      return { data: [], errors: [`Missing required columns: ${missingFields.join(", ")}`] };
    }

    const data = [];
    const errors = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(",").map(v => v.trim());
      if (values.length !== headers.length) {
        errors.push(`Row ${i + 1}: Column count mismatch`);
        continue;
      }

      const row = {};
      headers.forEach((header, idx) => {
        row[header] = values[idx];
      });

      if (!row.name || !row.email) {
        errors.push(`Row ${i + 1}: Missing name or email`);
        continue;
      }

      if (!row.email.includes("@")) {
        errors.push(`Row ${i + 1}: Invalid email format`);
        continue;
      }

      data.push({
        name: row.name,
        email: row.email,
        password: row.password || "Welcome123!",
        phone: row.phone || "",
        gender: row.gender || "",
        streetAddress: row.streetaddress || row.address || "",
        city: row.city || "",
        postalCode: row.postalcode || "",
        province: row.province || "Ontario",
        country: row.country || "Canada"
      });
    }

    return { data, errors };
  };

  const handleBulkParse = () => {
    const { data, errors } = parseCSV(bulkData);
    setBulkPreview(data);
    setBulkErrors(errors);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setBulkData(event.target.result);
    };
    reader.readAsText(file);
  };

  const handleBulkUpload = async () => {
    if (bulkPreview.length === 0) {
      toast.error("No valid members to upload");
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem("admin_token");
      const response = await fetch(`${API}/admin/members/bulk`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ members: bulkPreview })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Successfully added ${result.added} members`);
        if (result.failed > 0) {
          toast.warning(`${result.failed} members failed (duplicates or errors)`);
        }
        setShowBulkModal(false);
        setBulkData("");
        setBulkPreview([]);
        setBulkErrors([]);
        fetchMembers();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Bulk upload failed");
      }
    } catch (error) {
      toast.error("Error during bulk upload");
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const template = "name,email,password,phone,gender,streetaddress,city,postalcode,province,country\nJohn Doe,john@example.com,Password123,555-1234,Male,123 Main St,London,N6A 1A1,Ontario,Canada";
    const blob = new Blob([template], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "members_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredMembers = members.filter(member =>
    member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-members">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl text-kerala-green">Members Management</h1>
          <p className="text-gray-600 text-sm">{members.length} registered members</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowBulkModal(true)}
            variant="outline"
            className="cursor-pointer"
            data-testid="bulk-upload-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Bulk Upload
          </Button>
          <Button
            onClick={() => setShowAddModal(true)}
            className="btn-primary cursor-pointer"
            data-testid="add-member-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Member
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Search members..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Members Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-kerala-green text-white">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Email</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Phone</th>
                <th className="px-4 py-3 text-left text-sm font-medium">City</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Registered</th>
                <th className="px-4 py-3 text-center text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredMembers.map((member) => (
                <tr key={member.id} className="hover:bg-kerala-cream/50">
                  <td className="px-4 py-3 text-sm font-medium text-kerala-green">{member.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{member.email}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{member.phone || "-"}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{member.city || "-"}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {member.created_at ? new Date(member.created_at).toLocaleDateString() : "-"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteMember(member.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 cursor-pointer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
              {filteredMembers.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No members found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-heading text-xl text-kerala-green">Add New Member</h2>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddMember} className="p-6 space-y-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={newMember.name}
                  onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={newMember.email}
                  onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={newMember.password}
                  onChange={(e) => setNewMember({ ...newMember, password: e.target.value })}
                  required
                  placeholder="Min 8 characters"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={newMember.phone}
                  onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={newMember.city}
                  onChange={(e) => setNewMember({ ...newMember, city: e.target.value })}
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="flex-1 cursor-pointer">
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary flex-1 cursor-pointer">
                  Add Member
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-heading text-xl text-kerala-green">Bulk Upload Members</h2>
              <button onClick={() => setShowBulkModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Instructions */}
              <div className="bg-kerala-cream p-4 rounded-lg">
                <h3 className="font-medium text-kerala-green mb-2">CSV Format Instructions</h3>
                <p className="text-sm text-gray-600 mb-2">
                  Upload a CSV file with the following columns (name and email are required):
                </p>
                <code className="text-xs bg-white p-2 rounded block">
                  name, email, password, phone, gender, streetaddress, city, postalcode, province, country
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadTemplate}
                  className="mt-3 cursor-pointer"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </Button>
              </div>

              {/* File Upload */}
              <div>
                <Label>Upload CSV File</Label>
                <input
                  type="file"
                  accept=".csv"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-kerala-green file:text-white hover:file:bg-kerala-green/90 cursor-pointer"
                />
              </div>

              {/* Or paste CSV */}
              <div>
                <Label>Or Paste CSV Data</Label>
                <textarea
                  value={bulkData}
                  onChange={(e) => setBulkData(e.target.value)}
                  placeholder="Paste your CSV data here..."
                  className="mt-2 w-full h-32 p-3 border rounded-lg text-sm font-mono resize-none"
                />
              </div>

              {/* Parse Button */}
              <Button onClick={handleBulkParse} variant="outline" className="w-full cursor-pointer">
                Parse & Preview
              </Button>

              {/* Errors */}
              {bulkErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700 mb-2">
                    <AlertCircle className="w-4 h-4" />
                    <span className="font-medium">Errors Found</span>
                  </div>
                  <ul className="text-sm text-red-600 list-disc pl-5 space-y-1">
                    {bulkErrors.map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Preview */}
              {bulkPreview.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-green-50 px-4 py-2 flex items-center gap-2 text-green-700">
                    <Check className="w-4 h-4" />
                    <span className="font-medium">{bulkPreview.length} members ready to upload</span>
                  </div>
                  <div className="max-h-40 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Name</th>
                          <th className="px-3 py-2 text-left">Email</th>
                          <th className="px-3 py-2 text-left">City</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {bulkPreview.slice(0, 10).map((member, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-2">{member.name}</td>
                            <td className="px-3 py-2">{member.email}</td>
                            <td className="px-3 py-2">{member.city || "-"}</td>
                          </tr>
                        ))}
                        {bulkPreview.length > 10 && (
                          <tr>
                            <td colSpan={3} className="px-3 py-2 text-center text-gray-500">
                              ... and {bulkPreview.length - 10} more
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Upload Button */}
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowBulkModal(false);
                    setBulkData("");
                    setBulkPreview([]);
                    setBulkErrors([]);
                  }}
                  className="flex-1 cursor-pointer"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleBulkUpload}
                  disabled={bulkPreview.length === 0 || uploading}
                  className="btn-primary flex-1 cursor-pointer"
                >
                  {uploading ? "Uploading..." : `Upload ${bulkPreview.length} Members`}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
